export const defectLabels = {
  broken_back: 'Broken seat back', broken_bottom: 'Broken seat bottom',
  cup_holder: 'Broken / missing cup holder', upholstery: 'Damaged upholstery', discoloration: 'Discolored seat',
} as const;
export type DefectType = keyof typeof defectLabels;
export type ReviewStatus = 'unreviewed' | 'needs_attention' | 'dismissed' | 'closer_inspection';
export const reviewLabels: Record<ReviewStatus, string> = {
  unreviewed: 'Unreviewed', needs_attention: 'Needs attention', dismissed: 'Dismissed', closer_inspection: 'Needs closer inspection',
};
export interface SeatFinding {
  id: string; seatId: string; defect: DefectType; description: string;
  section?: string; row?: string; seatNumber?: string;
  evidence?: { imageUrl: string; caption: string };
}
export interface SeatInspection {
  version: 1; id: string; sceneId: string; title: string; capturedAt: string | null;
  sample: boolean; coverage: { assessedSeats: number | null; totalSeats: number | null };
  findings: SeatFinding[];
}
const record = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const nonempty = (v: unknown): v is string => typeof v === 'string' && !!v.trim();
const count = (v: unknown) => v === null || (typeof v === 'number' && Number.isSafeInteger(v) && v >= 0);
export function parseSeatInspection(v: unknown): SeatInspection {
  if (!record(v) || v.version !== 1 || !nonempty(v.id) || !nonempty(v.sceneId) || !nonempty(v.title)
    || typeof v.sample !== 'boolean' || !(v.capturedAt === null || (typeof v.capturedAt === 'string' && Number.isFinite(Date.parse(v.capturedAt))))
    || !record(v.coverage) || !count(v.coverage.assessedSeats) || !count(v.coverage.totalSeats)
    || !Array.isArray(v.findings) || v.findings.length > 100000) throw new Error('Invalid inspection header or coverage');
  if (typeof v.coverage.assessedSeats === 'number' && typeof v.coverage.totalSeats === 'number'
    && v.coverage.assessedSeats > v.coverage.totalSeats) throw new Error('Assessed seats exceed total seats');
  const ids = new Set<string>();
  for (const f of v.findings) {
    if (!record(f) || !nonempty(f.id) || ids.has(f.id) || !nonempty(f.seatId)
      || typeof f.defect !== 'string' || !Object.prototype.hasOwnProperty.call(defectLabels, f.defect) || typeof f.description !== 'string'
      || ['section', 'row', 'seatNumber'].some(k => f[k] !== undefined && !nonempty(f[k]))) throw new Error('Invalid or duplicate finding');
    if (f.evidence !== undefined && (!record(f.evidence) || !nonempty(f.evidence.imageUrl)
      || !/^(https?:\/\/|\/[^/])/.test(f.evidence.imageUrl) || typeof f.evidence.caption !== 'string')) throw new Error('Invalid evidence');
    ids.add(f.id);
  }
  return v as unknown as SeatInspection;
}
